import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import ErrorBoundary from "~/components/ErrorBoundary";
import MobileSubNav from "~/components/MobileSubNav";
import { NavLinkWrapper, MainNavLinkDefault } from "~/components/Header";

import FaIcon from "~/components/FaIcon";

import { clearAllBodyScrollLocks } from "body-scroll-lock";
import trackNavigationEvent from "~/utilities/trackNavigationEvent";
import transition from "~/utilities/transition";
import { sanitizeForId } from "~/utilities/helpers";

export const MainNavWithSubNav = styled.button`
    ${transition({}, "background-color")};
    font-size: 2rem;
    font-weight: ${({ bold }) => (bold ? `800` : `400`)};
    color: ${({ theme }) => theme.colors.typography.defaultLightBackground};
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 10px 20px;
    display: ${({ hidden }) => (hidden ? "none" : "flex")};

    &:hover,
    &:focus {
        background-color: ${({ theme }) => theme.palette.grayFive};
    }
`;

export const MainNavWithSubNavIcon = styled(FaIcon)`
    font-size: 2rem;
    color: ${({ theme }) => theme.palette.black};

    @media (forced-colors: active) {
        color: ButtonText;
    }
`;

const MainMobileNavigationItem = ({
    linkLabel,
    linkUrl,
    linkNativeName = null,
    newTabOverride = "default",
    subItems = [],
    index,
    dispatch,
    openedSubnavs = {},
    tagRef = {},
    menuOpened = false,
    dropdownLabel,
}) => {
    const [focusElementId, setFocusElementId] = useState("");
    const hasDropdown = subItems && !!subItems.length;
    const submenuId = `${sanitizeForId(linkLabel)}-${index}`;
    const isNavOpened = openedSubnavs.secondary === submenuId;

    const mobileNavClick = (clickEvent) => {
        clickEvent.preventDefault();
        trackNavigationEvent(true, 1, linkLabel, false);
        dispatch({
            type: "HANDLE_SUB_NAV",
            id: `${sanitizeForId(linkLabel)}-${index}`,
            currentSubNavs: openedSubnavs,
            level: "secondary",
        });
    };

    useEffect(() => {
        if (!menuOpened && focusElementId) {
            const element = document.getElementById(focusElementId);
            element.focus();
        }

        if (menuOpened && focusElementId) {
            setFocusElementId("");
        }
    }, [menuOpened, focusElementId]);

    return (
        <NavLinkWrapper>
            {!!hasDropdown ? (
                <ErrorBoundary>
                    <MainNavWithSubNav
                        type="button"
                        onClick={mobileNavClick}
                        aria-label={`${dropdownLabel ?? linkLabel} submenu`}
                        id={submenuId}
                        ref={index === 0 ? tagRef : null}
                        hidden={menuOpened}
                    >
                        {dropdownLabel ?? linkLabel}
                        <MainNavWithSubNavIcon name="arrow-right" />
                    </MainNavWithSubNav>

                    <MobileSubNav
                        level="secondary"
                        isOpen={isNavOpened}
                        items={subItems}
                        firstLinkLabelIndex={index}
                        firstLinkLabel={linkLabel}
                        parentLinkNativeName={linkNativeName}
                        {...{
                            linkUrl,
                            linkLabel,
                            newTabOverride,
                            clearAllBodyScrollLocks,
                            openedSubnavs,
                            dispatch,
                            setFocusElementId,
                        }}
                    />
                </ErrorBoundary>
            ) : (
                <MainNavLinkDefault
                    {...{ newTabOverride }}
                    hidden={menuOpened}
                    to={linkUrl}
                    onClick={() => {
                        trackNavigationEvent(false, 1, linkLabel, false);
                        clearAllBodyScrollLocks();
                    }}
                >
                    {linkLabel}
                </MainNavLinkDefault>
            )}
        </NavLinkWrapper>
    );
};

MainMobileNavigationItem.propTypes = {
    linkLabel: PropTypes.string.isRequired,
    linkUrl: PropTypes.string.isRequired,
    linkNativeName: PropTypes.string,
    newTabOverride: PropTypes.string,
    subItems: PropTypes.arrayOf(PropTypes.shape({})),
    index: PropTypes.number.isRequired,
    dispatch: PropTypes.func.isRequired,
    openedSubnavs: PropTypes.shape({}),
    tagRef: PropTypes.object,
    menuOpened: PropTypes.bool,
    dropdownLabel: PropTypes.string,
};

export default MainMobileNavigationItem;
